import { Box, Stack, Typography } from '@mui/material';
import { useCurrentHangout, useMyEntry } from '../hooks/useHangout';
import { formatTokens } from '../lib/format';
import { colors, mono } from '../themes/theme';

/**
 * Always-visible balance, pinned to the top of the app.
 *
 * It lived in the board's header beside the hangout name, where a long name --
 * condensed, uppercase and unbreakable -- overflowed and covered the one number
 * people keep checking. At app level nothing can compete with it, and it stays
 * on screen while browsing a market or the standings.
 */
export function TopBar() {
    const hangout = useCurrentHangout();
    const entry = useMyEntry(hangout.data?.id);

    const eliminated = entry.data?.eliminatedAt != null;

    return (
        <Box
            component="header"
            sx={{
                position: 'sticky',
                top: 0,
                zIndex: 20,
                bgcolor: colors.ground,
                borderBottom: `1px solid ${colors.line}`,
                // Sits below the notch rather than under it.
                pt: 'env(safe-area-inset-top)',
                mx: -2,
                px: 2,
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ minHeight: 44, maxWidth: 560, mx: 'auto', width: '100%' }}
            >
                <Typography
                    sx={{
                        fontFamily: mono,
                        fontSize: '0.68rem',
                        letterSpacing: '0.14em',
                        textTransform: 'uppercase',
                        color: colors.muted,
                        // Yields to the balance if the name is long.
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                    }}
                >
                    {hangout.data?.name ?? 'Pony Up'}
                </Typography>

                <Stack direction="row" alignItems="center" gap={0.5} sx={{ flexShrink: 0 }}>
                    {entry.isSuccess && (
                        <Stack direction="row" alignItems="baseline" gap={0.75}>
                            <Typography
                                sx={{
                                    fontFamily: mono,
                                    fontSize: '0.6rem',
                                    letterSpacing: '0.12em',
                                    textTransform: 'uppercase',
                                    color: colors.muted,
                                }}
                            >
                                {eliminated ? 'Out' : 'Balance'}
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: mono,
                                    fontVariantNumeric: 'tabular-nums',
                                    fontWeight: 600,
                                    fontSize: '1.05rem',
                                    lineHeight: 1,
                                    whiteSpace: 'nowrap',
                                    color: eliminated ? colors.muted : colors.ink,
                                    textDecoration: eliminated ? 'line-through' : 'none',
                                }}
                            >
                                {formatTokens(entry.data.balance)}
                            </Typography>
                        </Stack>
                    )}
                </Stack>
            </Stack>
        </Box>
    );
}
